/* TDX enquiry form: autocomplete + part-aware questions (m11 Phase 1).

   Shared by every storefront enquiry form (snippets/tdx-enquiry-fields.liquid
   renders the markup): the product-page form (Form-snippet), the contact page
   (sections/contact-form via Form-snippet) and the breaking-vehicle page
   (sections/vehicle-enquiry-form).

   Each form root carries data-tdx-enquiry="<id prefix>" and
   data-tdx-cls="<css class prefix>", so one copy of this file drives forms that
   keep their own look.

   What it does, per form:
     1. Make/model and part-name autocomplete from assets/vehicle-form-data.json
        (`models`, `parts`).
     2. Below Part needed, driven by `part_info` ({sided, pos?, extra?, q?}):
          - a Side/position row when the entry is sided (its own `pos` words:
            headlight Left/Right, door 4 corners, camera Front/Rear...);
          - one row per `extra` ({label, values, optional?}), e.g. "Inner or
            outer" on rear lights. An `optional` row stays folded behind a
            small "+ label" button until the customer opens it;
          - up to two part-detail rows (`q`).
        A part with NO entry takes NO side (owner, 2026-10-18; it was the
        other way round in m11). There are no name rules here on purpose:
        which parts take a side, and which words, live in ONE place (the
        extension's positions table, built into part_info by inventory-api
        scripts/build_form_data.py). A second copy in theme JS is what drifts.
     3. Writes the answers into three hidden inputs that sit above Message:
          contact[Side]                 "Left (N/S)"
          contact[Part Details]         "Inner or outer: Outer; Bulb type: LED"
          contact[Condition Preference] "Any" | "Good used" | "Best available"
        Side / Part Details are disabled while empty, so they are not mailed at
        all rather than mailed blank. Never add a per-attribute mail label: the
        mail parser glues an unknown label onto the field before it.
     4. Pre-select (2026-11-03). On a product page the form root carries what
        the listing says about itself (data-tdx-pf-lr / -fr / -pos2 / -bulb,
        worked out in snippets/Form-snippet.liquid). While Part needed still
        holds the listing's own part, a chip is pressed when one of those words
        is EXACTLY its text; nothing else is guessed, and the customer can
        change or clear it. If the listing's ebay.part_type is not a part_info
        name, its part-type tag (data-tdx-pf-key) is looked up instead, again
        only while Part needed is untouched.
     5. Colour (2026-11-03). A painted listing (Form-snippet sets
        data-tdx-pf-painted from its part-type tag: doors, wings, bumpers,
        bonnets, tailgates, wing mirrors...) gets a free-text Colour box,
        pre-filled by colourOf() only when the listing is clear. It travels in
        Part Details as "Colour: Polar White (PYW)"; no new mail label.

   If the data file fails to load, nothing here blocks the submit: the fields
   are ordinary inputs, Condition still works (its chips are server-rendered)
   and Side/Part Details simply stay out of the mail.

   window.TDXFormData is the one fetch of the data file for the page; the
   vehicle-enquiry section reads `breaking` from it for its own prefill. */
(function () {
  'use strict';

  var MAX_RESULTS = 8;

  /* Every form renders the <script> tag; only the first copy runs. */
  if (window.TDXEnquiryInit) return;
  window.TDXEnquiryInit = true;

  var roots = document.querySelectorAll('[data-tdx-enquiry]');
  if (!roots.length) return;

  var dataUrl = roots[0].getAttribute('data-tdx-data-url');
  if (!window.TDXFormData) {
    window.TDXFormData = fetch(dataUrl)
      .then(function (r) { if (!r.ok) throw new Error('form data ' + r.status); return r.json(); });
  }

  for (var i = 0; i < roots.length; i++) setupConditionRow(roots[i]);

  window.TDXFormData
    .then(function (data) {
      var idx = buildIndexes(data || {});
      var info = buildPartInfo(data && data.part_info);
      for (var i = 0; i < roots.length; i++) {
        var root = roots[i];
        var fid = root.getAttribute('data-tdx-enquiry');
        var cls = root.getAttribute('data-tdx-cls');
        setupAutocomplete(fid + '-vehicle', idx.vehicles, cls, false);
        setupAutocomplete(fid + '-part', idx.parts, cls, true);
        if (info) setupPartQuestions(root, fid, info);
      }
    })
    .catch(function () { /* the form works without autocomplete or part questions */ });

  /* ── Indexes ───────────────────────────────────────────────────────── */
  function buildIndexes(formData) {
    var vehicles = [], parts = [];
    var models = formData.models || {};
    Object.keys(models).forEach(function (make) {
      Object.keys(models[make]).forEach(function (model) {
        (models[make][model] || []).forEach(function (gen) {
          vehicles.push({
            text: make + ' ' + model,
            detail: gen.gen + ' (' + gen.code + ') ' + gen.years,
            search: (make + ' ' + model + ' ' + gen.gen + ' ' + gen.code + ' ' + gen.years).toLowerCase(),
            value: make + ' ' + model + ' ' + gen.gen + ' (' + gen.code + ')'
          });
        });
      });
    });
    (formData.parts || []).forEach(function (p) {
      parts.push({ text: p, detail: '', search: p.toLowerCase(), value: p });
    });
    return { vehicles: vehicles, parts: parts };
  }

  function norm(s) {
    return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').replace(/\s+/g, ' ').trim();
  }

  /* part_info keyed by normalised name, aliases folded in. */
  function buildPartInfo(raw) {
    if (!raw || typeof raw !== 'object') return null;
    var byName = {};
    Object.keys(raw).forEach(function (k) {
      var e = raw[k];
      if (!e || typeof e !== 'object') return;
      byName[norm(k)] = e;
      (e.aliases || []).forEach(function (a) {
        var n = norm(a);
        if (n && !byName[n]) byName[n] = e;
      });
    });
    return byName;
  }

  /* The entry for what the customer typed, or null for "unknown" (= sided).
     Exact name or alias first, then a singular retry ("headlights"). Bare
     head nouns ("engine", "door", "rear light") have their own entries, built
     in inventory-api scripts/build_form_data.py, so there is no guessing here. */
  function lookup(info, typed) {
    var k = norm(typed);
    if (!k) return null;
    if (info[k]) return info[k];
    if (k.length > 3 && k.slice(-1) === 's' && info[k.slice(0, -1)]) return info[k.slice(0, -1)];
    return null;
  }

  /* ── Part questions ────────────────────────────────────────────────── */
  function setupPartQuestions(root, fid, info) {
    var input = document.getElementById(fid + '-part');
    var box = root.querySelector('[data-tdx-pi]');
    var side = root.querySelector('[data-tdx-hidden="side"]');
    var details = root.querySelector('[data-tdx-hidden="details"]');
    if (!input || !box || !side || !details) return;

    var current = null;   /* the questions on screen: [{kind, label, multi, el}] */
    var lastKey = null;

    /* What this listing says about itself (product pages only). */
    var pf = {
      part: norm(root.getAttribute('data-tdx-pf-part')),
      key: root.getAttribute('data-tdx-pf-key') || '',
      lr: root.getAttribute('data-tdx-pf-lr') || '',
      fr: root.getAttribute('data-tdx-pf-fr') || '',
      pos2: root.getAttribute('data-tdx-pf-pos2') || '',
      bulb: root.getAttribute('data-tdx-pf-bulb') || '',
      painted: root.getAttribute('data-tdx-pf-painted') === '1'
    };
    var ANSWERS = { 'Bulb type': pf.bulb };
    var cls = root.getAttribute('data-tdx-cls') || 'tdx-enquiry';
    pf.colour = pf.painted ? colourOf({
      title: root.getAttribute('data-tdx-pf-title'),
      pc: root.getAttribute('data-tdx-pf-pc'),
      vcc: root.getAttribute('data-tdx-pf-vcc'),
      vcn: root.getAttribute('data-tdx-pf-vcn'),
      co: root.getAttribute('data-tdx-pf-co')
    }) : '';

    /* Free-text Colour box for painted parts. Not a chip row: there are
       hundreds of paint names, and the customer's car may be a different
       colour from this listing. It has no name="", so it is never mailed by
       itself; sync() puts it into Part Details as "Colour: ...". */
    function colourBox(value) {
      var wrap = document.createElement('div');
      wrap.setAttribute('data-group', 'colour');
      wrap.className = 'tdx-pi__group';
      var lab = document.createElement('label');
      lab.className = 'tdx-pi__label';
      lab.htmlFor = fid + '-pi-colour';
      lab.textContent = 'Colour';
      var inp = document.createElement('input');
      inp.type = 'text';
      inp.id = fid + '-pi-colour';
      inp.className = cls + '__input tdx-pi__text';
      inp.maxLength = 60;
      inp.autocomplete = 'off';
      inp.placeholder = 'Colour name or paint code, e.g. Polar White PYW';
      inp.value = value || '';
      var hint = document.createElement('p');
      hint.className = 'tdx-pi__hint';
      hint.id = fid + '-pi-colour-hint';
      hint.textContent = value
        ? 'Taken from this listing. If your car is a different colour, change it.'
        : 'Optional. Helps us match the paint.';
      inp.setAttribute('aria-describedby', hint.id);
      wrap.appendChild(lab);
      wrap.appendChild(inp);
      wrap.appendChild(hint);
      return { wrap: wrap, input: inp };
    }

    function chipRow(label, values, multi, group) {
      var wrap = document.createElement('div');
      wrap.setAttribute('data-group', group);
      wrap.className = 'tdx-pi__group';
      var lab = document.createElement('p');
      lab.className = 'tdx-pi__label';
      lab.id = fid + '-pi-' + group;
      lab.textContent = label;
      var row = document.createElement('div');
      row.className = 'tdx-pi__chips';
      row.setAttribute('role', 'group');
      row.setAttribute('aria-labelledby', lab.id);
      if (multi) row.setAttribute('data-multi', '1');
      values.forEach(function (v) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'tdx-pi__chip';
        b.setAttribute('aria-pressed', 'false');
        b.textContent = v;
        row.appendChild(b);
      });
      wrap.appendChild(lab);
      wrap.appendChild(row);
      return { wrap: wrap, row: row };
    }

    function render() {
      var key = norm(input.value);
      if (key === lastKey) return;
      lastKey = key;
      box.innerHTML = '';
      current = [];
      if (!key) { box.hidden = true; sync(); return; }

      var own = !!pf.part && key === pf.part;   /* still the listing's own part */
      var e = lookup(info, input.value) || (own && pf.key ? lookup(info, pf.key) : null);
      /* Read `sided`, not whether an entry exists (m12 contract, 2026-10-18):
           no entry          -> no side, no extra rows, no questions;
           sided: false      -> no side, but its questions (alloy wheel, gearbox...);
           sided: true       -> Side chips from its own `pos`, plus its `extra` rows. */
      var sided = !!(e && e.sided === true);
      var pos = (sided && e.pos && e.pos.length) ? e.pos : null;
      if (pos) {
        var r = chipRow('Side / position', pos, false, 'side');
        box.appendChild(r.wrap);
        current.push({ kind: 'side', label: null, multi: false, el: r.row });
      }
      ((sided && e.extra) || []).forEach(function (x, n) {
        if (!x || !x.label || !x.values || !x.values.length) return;
        var r = chipRow(x.label, x.values, false, 'x' + n);
        if (x.optional) {
          /* Folded until asked for: most customers do not know, and an
             unanswered row is noise. Opening it never preselects a chip. */
          var more = document.createElement('button');
          more.type = 'button';
          more.className = 'tdx-pi__more';
          more.setAttribute('aria-expanded', 'false');
          more.textContent = '+ ' + x.label + ' (optional)';
          r.wrap.hidden = true;
          r.more = more;
          more.addEventListener('click', function () {
            r.wrap.hidden = false;
            if (more.parentNode) more.parentNode.removeChild(more);
            var first = r.row.querySelector('.tdx-pi__chip');
            if (first) first.focus();
          });
          box.appendChild(more);
        }
        box.appendChild(r.wrap);
        current.push({ kind: 'extra', label: x.label, multi: false, el: r.row, wrap: r.wrap, more: r.more });
      });
      ((e && e.q) || []).slice(0, 2).forEach(function (q, n) {
        if (!q || !q.label || !q.values || !q.values.length) return;
        var r = chipRow(q.label, q.values, !!q.multi, 'q' + n);
        box.appendChild(r.wrap);
        current.push({ kind: 'q', label: q.label, multi: !!q.multi, el: r.row });
      });
      /* Colour: this listing's painted part (Liquid decides from its
         part-type tag), or a part_info entry that says painted: true once
         inventory-api build_form_data.py carries that flag. */
      if ((own && pf.painted) || (e && e.painted === true)) {
        var cb = colourBox(own ? pf.colour : '');
        box.appendChild(cb.wrap);
        current.push({ kind: 'text', label: 'Colour', multi: false, el: cb.input });
      }
      box.hidden = !current.length;
      if (own) preselect();
      sync();
    }

    /* Press the chip whose text is exactly one of the listing's own words. */
    function press(row, words) {
      var chips = row.querySelectorAll('.tdx-pi__chip');
      for (var w = 0; w < words.length; w++) {
        if (!words[w]) continue;
        for (var i = 0; i < chips.length; i++) {
          if (chips[i].textContent === words[w]) { chips[i].setAttribute('aria-pressed', 'true'); return true; }
        }
      }
      return false;
    }

    function preselect() {
      (current || []).forEach(function (c) {
        if (c.kind === 'side') {
          press(c.el, [pf.fr && pf.lr ? pf.fr + ' ' + pf.lr : '', pf.lr, pf.fr]);
        } else if (c.kind === 'extra') {
          if (press(c.el, [pf.pos2, pf.fr]) && c.more) {
            c.wrap.hidden = false;   /* an answered optional row is shown, not folded */
            if (c.more.parentNode) c.more.parentNode.removeChild(c.more);
          }
        } else if (c.kind === 'q' && !c.multi) {
          press(c.el, [ANSWERS[c.label] || '']);
        }
      });
    }

    function picked(row) {
      var out = [];
      var on = row.querySelectorAll('[aria-pressed="true"]');
      for (var i = 0; i < on.length; i++) out.push(on[i].textContent);
      return out.join(', ');
    }

    function sync() {
      var s = '', d = [];
      (current || []).forEach(function (c) {
        /* ";" splits Part Details in inventory-api split_part_details() */
        var v = c.kind === 'text' ? c.el.value.replace(/[;\r\n]+/g, ',').replace(/\s+/g, ' ').trim() : picked(c.el);
        if (!v) return;
        if (c.label === null) s = v;
        else d.push(c.label + ': ' + v);
      });
      setHidden(side, s);
      setHidden(details, d.join('; '));
    }

    box.addEventListener('click', function (ev) {
      var b = ev.target.closest('.tdx-pi__chip');
      if (!b) return;
      toggle(b);
      sync();
    });
    box.addEventListener('input', sync);   /* the Colour box */
    input.addEventListener('input', render);
    input.addEventListener('change', render);
    render();   /* the product-page form arrives with Part needed prefilled */
  }

  /* ── Colour (body panels, 2026-11-03) ──────────────────────────────── */
  /* What colour this listing's panel is, as one line for the Colour box:
     "Polar White (PYW)", "Black (Z11)", "Grey", or '' when the listing is not
     clear. src = {title, pc, vcc, vcn, co}:
       pc   ebay.paint_code               the part's own code (inventory-api #808)
       vcc  enriched.vehicle_colour_code  the DONOR CAR's code
       vcn  enriched.vehicle_colour_name  the donor car's colour name
       co   ebay.colour                   the part's eBay Colour (Black, Grey...)
     The donor car's colour is not always the part's (a Qashqai J11 mirror cap
     is Z11 black on a GAB purple car), so the part's own code wins, and the
     title has a veto: a colour word in the title that disagrees with the
     answer means no answer. Primer / unpainted / textured / plastic = no
     answer. Measured on the live store 2026-11-03: see the PR notes. */
  var FAMILY = {
    black: /\b(BLACK|EBONY|ONYX|OBSIDIAN)\b/, white: /\b(WHITE|IVORY|BLANC|SNOW)\b/,
    silver: /\b(SILVER|ALUMINIUM|PLATINUM)\b/, grey: /\b(GREY|GRAY|GRAPHITE|TITANIUM|GUNMETAL|CHARCOAL|SLATE)\b/,
    red: /\b(RED|MAROON|BURGUNDY|CRIMSON|RUBY)\b/, blue: /\b(BLUE|NAVY|AQUA|AZURE)\b/,
    purple: /\b(PURPLE|VIOLET|AMETHYST|NIGHTSHADE|NIGHT SHADE)\b/, orange: /\bORANGE\b/,
    brown: /\b(BROWN|BRONZE|COPPER|MOCHA)\b/, beige: /\b(BEIGE|SAND|CHAMPAGNE)\b/,
    green: /\b(GREEN|OLIVE|LIME)\b/, yellow: /\bYELLOW\b/, gold: /\b(GOLD|BRASS)\b/
  };
  var TITLE_WORDS = /\b(BLACK|WHITE|SILVER|GREY|GRAY|RED|BLUE|PURPLE|ORANGE|BROWN|BEIGE|GREEN|YELLOW|GOLD|BRONZE|MAROON)\b/g;
  var NO_COLOUR = /\b(PRIMER|PRIMED|UNPAINTED|UNPAINT|PAINT TO MATCH|NEEDS? PAINT|MAY VARY|TEXTURED|PLASTIC|RAW)\b/;

  function families(s) {
    var out = {}, n = 0;
    s = String(s || '').toUpperCase();
    Object.keys(FAMILY).forEach(function (f) { if (FAMILY[f].test(s)) { out[f] = 1; n++; } });
    return n ? out : null;
  }
  function overlap(a, b) {
    for (var k in a) if (b[k]) return true;
    return false;
  }
  /* A paint code is short and code-shaped (Z11, 1F7, PYW, NH700M, R3W), not a word. */
  function codeShape(c) {
    return /^[A-Z0-9]{2,7}$/.test(c) && (/[0-9]/.test(c) || c.length <= 3);
  }
  function tidy(s) {
    s = String(s || '').replace(/\s+/g, ' ').trim();
    if (s && s === s.toUpperCase()) s = s.toLowerCase().replace(/\b[a-z]/g, function (m) { return m.toUpperCase(); });
    return s;
  }

  function colourOf(src) {
    var t = ' ' + String(src.title || '').toUpperCase().replace(/[^A-Z0-9]+/g, ' ') + ' ';
    if (NO_COLOUR.test(t)) return '';
    var inTitle = function (c) { return !!c && t.indexOf(' ' + c + ' ') !== -1; };

    /* ebay.paint_code may hold "R3W ELEMENTAL BRASS METALLIC" or "AHV, 1K". */
    var pcs = String(src.pc || '').toUpperCase().split(/[,;\/]+/).map(function (s) { return s.trim().split(/\s+/)[0]; }).filter(codeShape);
    var vc = String(src.vcc || '').trim().toUpperCase();
    if (!codeShape(vc)) vc = '';
    var code = '', donor = false;
    if (pcs.length > 1) {
      if (vc && pcs.indexOf(vc) !== -1) { code = vc; donor = true; } else return '';
    } else if (pcs.length === 1 && vc && pcs[0] !== vc) {
      if (inTitle(pcs[0]) && !inTitle(vc)) code = pcs[0];
      else if (inTitle(vc) && !inTitle(pcs[0])) { code = vc; donor = true; }
      else return '';
    } else if (pcs.length === 1) {
      code = pcs[0]; donor = pcs[0] === vc;
    } else if (vc) {
      code = vc; donor = true;
    }

    var co = tidy(src.co), vcn = tidy(src.vcn);
    var name = (donor && vcn) ? vcn : co;
    /* the donor's name must not contradict the part's own eBay Colour */
    if (donor && vcn && co) {
      var a = families(vcn), b = families(co);
      if (a && b && !overlap(a, b) && !inTitle(code)) return '';
    }

    var words = t.match(TITLE_WORDS) || [];
    var tf = words.length ? families(words.join(' ')) : null;
    var tn = tf ? Object.keys(tf).length : 0;
    if (!name && !code) {
      /* nothing in the data: one clear colour word in the title will do */
      return tn === 1 && words.length >= 1 ? tidy(words[0]) : '';
    }
    if (tf && !inTitle(code)) {
      var nf = families(name);
      if (tn > 1) return '';
      if (nf && !overlap(nf, tf)) return '';
      if (!nf && !name) name = tidy(words[0]);
    }
    if (name && code) return name + ' (' + code + ')';
    return name || code;
  }

  function setHidden(el, v) {
    el.value = v;
    el.disabled = !v;   /* disabled inputs are not submitted: absent, not blank */
  }

  function toggle(b) {
    var row = b.parentNode;
    var on = b.getAttribute('aria-pressed') === 'true';
    if (!row.hasAttribute('data-multi')) {
      var all = row.querySelectorAll('.tdx-pi__chip');
      for (var i = 0; i < all.length; i++) all[i].setAttribute('aria-pressed', 'false');
    }
    b.setAttribute('aria-pressed', on ? 'false' : 'true');
  }

  /* Condition is server-rendered, so it works even when the data file does not. */
  function setupConditionRow(root) {
    var row = root.querySelector('[data-tdx-cond]');
    var hidden = root.querySelector('[data-tdx-hidden="condition"]');
    if (!row || !hidden) return;
    row.addEventListener('click', function (ev) {
      var b = ev.target.closest('.tdx-pi__chip');
      if (!b) return;
      var all = row.querySelectorAll('.tdx-pi__chip');
      for (var i = 0; i < all.length; i++) all[i].setAttribute('aria-pressed', String(all[i] === b));
      hidden.value = b.getAttribute('data-value') || b.textContent;
    });
  }

  /* ── Autocomplete (moved here from the two form files, unchanged in behaviour) ── */
  function filterOptions(query, options) {
    if (!query || query.length < 1) return [];
    var words = query.toLowerCase().split(/\s+/);
    var matches = [];
    for (var i = 0; i < options.length; i++) {
      var s = options[i].search, allMatch = true, bestPos = s.length;
      for (var j = 0; j < words.length; j++) {
        if (!words[j]) continue;
        var pos = s.indexOf(words[j]);
        if (pos === -1) { allMatch = false; break; }
        if (pos < bestPos) bestPos = pos;
      }
      if (allMatch) matches.push({ idx: i, pos: bestPos, len: s.length });
    }
    matches.sort(function (a, b) { return a.pos !== b.pos ? a.pos - b.pos : a.len - b.len; });
    var result = [];
    for (var k = 0; k < Math.min(matches.length, MAX_RESULTS); k++) result.push(options[matches[k].idx]);
    return result;
  }

  function setupAutocomplete(inputId, options, cls, isPart) {
    var input = document.getElementById(inputId);
    var dropdown = document.getElementById(inputId + '-list');
    if (!input || !dropdown) return;
    var OPEN = cls + '__dropdown--open', ACTIVE = cls + '__dropdown-active';
    var activeIdx = -1, currentMatches = [];

    function show(matches) {
      currentMatches = matches; activeIdx = -1;
      if (matches.length === 0) {
        if (input.value.length >= 2 && isPart) {
          dropdown.innerHTML = '<li class="' + cls + '__dropdown-empty">Not in our list? Just type your part name</li>';
          dropdown.classList.add(OPEN);
        } else { hide(); }
        input.setAttribute('aria-expanded', 'false');
        return;
      }
      var html = '';
      for (var i = 0; i < matches.length; i++) {
        var m = matches[i];
        html += '<li role="option" id="' + inputId + '-opt-' + i + '" data-idx="' + i + '">';
        html += '<span class="' + cls + '__dropdown-text">' + esc(m.text) + '</span>';
        if (m.detail) html += ' <span class="' + cls + '__dropdown-detail">' + esc(m.detail) + '</span>';
        html += '</li>';
      }
      dropdown.innerHTML = html;
      dropdown.classList.add(OPEN);
      input.setAttribute('aria-expanded', 'true');
    }
    function hide() {
      dropdown.classList.remove(OPEN);
      dropdown.innerHTML = ''; activeIdx = -1; currentMatches = [];
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
    }
    function selectItem(idx) {
      if (idx >= 0 && idx < currentMatches.length) {
        input.value = currentMatches[idx].value; hide();
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    function setActive(idx) {
      var items = dropdown.querySelectorAll('[role="option"]');
      for (var i = 0; i < items.length; i++) items[i].classList.remove(ACTIVE);
      if (idx >= 0 && idx < items.length) {
        items[idx].classList.add(ACTIVE);
        items[idx].scrollIntoView({ block: 'nearest' });
        input.setAttribute('aria-activedescendant', items[idx].id); activeIdx = idx;
      } else { activeIdx = -1; input.removeAttribute('aria-activedescendant'); }
    }
    input.addEventListener('input', function () { show(filterOptions(input.value, options)); });
    input.addEventListener('focus', function () { if (input.value.length >= 1) show(filterOptions(input.value, options)); });
    input.addEventListener('keydown', function (e) {
      if (!dropdown.classList.contains(OPEN)) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive(activeIdx < currentMatches.length - 1 ? activeIdx + 1 : 0); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(activeIdx > 0 ? activeIdx - 1 : currentMatches.length - 1); }
      else if (e.key === 'Enter') { if (activeIdx >= 0) { e.preventDefault(); selectItem(activeIdx); } }
      else if (e.key === 'Escape') { hide(); }
    });
    document.addEventListener('click', function (e) {
      if (!input.contains(e.target) && !dropdown.contains(e.target)) hide();
    });
    dropdown.addEventListener('click', function (e) {
      var li = e.target.closest('[role="option"]');
      if (li) selectItem(parseInt(li.getAttribute('data-idx'), 10));
    });
    dropdown.addEventListener('mouseover', function (e) {
      var li = e.target.closest('[role="option"]');
      if (li) setActive(parseInt(li.getAttribute('data-idx'), 10));
    });
  }

  function esc(s) { var d = document.createElement('div'); d.textContent = s; return d.innerHTML; }
})();
